/**
 * CloudLocalCostDisplay Component
 *
 * THE single, standardized cost cell shared by every cost surface in the app:
 * TokenEstimateCard (single-operation tabs, compact + full) and
 * FullPipelineCostCard (the full video pipeline header). Centralising it here is
 * what makes the "what runs free locally vs. what is billed on cloud" display
 * identical everywhere instead of each tab reinventing it.
 *
 * Behaviour:
 *   - showLocal=true  → dual columns: "Cloud {cloudTokens}" + "Local GPU Free/{n}".
 *     Used on desktop (Electron) when the selected provider can run on the user's
 *     own GPU — the user sees both what cloud would charge AND their local price.
 *   - showLocal=false → single "Cloud {cloudTokens}" value. Used on web (no local
 *     hardware) and for cloud-only providers (no phantom local discount).
 *
 * Visual pattern: right-aligned stacked layout (label on top, value below).
 */

import { memo } from 'react';
import { Cpu, Cloud, Loader2 } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import sharedConstants from '../../constants/shared_constants.json';

interface CloudLocalCostDisplayProps {
  /** Full cloud price (no local/BYOK discount) — always billed when run on Asgard's GPU. */
  cloudTokens: number;
  /** Local price: 0 = Free under the 100% local-GPU discount. */
  localTokens: number;
  /**
   * Show the dual Cloud + Local GPU columns. Caller computes this as
   * `isElectronMode() && providerCanRunLocally` so the local "Free" column only
   * appears on desktop for providers that actually run on the user's GPU.
   */
  showLocal: boolean;
  /** Color the headline value red when the user cannot afford it. */
  hasEnough: boolean;
  /** Prefix such as "Up to " for ceiling (ai_director) estimates. */
  prefix?: string;
  /** Inline spinner next to the label while a newer estimate is being fetched. */
  loading?: boolean;
  /** Label for the single/cloud value (defaults to "Cost"). */
  cloudLabel?: string;
}

export const CloudLocalCostDisplay = memo(function CloudLocalCostDisplay({
  cloudTokens,
  localTokens,
  showLocal,
  hasEnough,
  prefix = '',
  loading = false,
  cloudLabel,
}: CloudLocalCostDisplayProps) {
  const { t } = useTranslation();
  const localGpuDiscount = sharedConstants.constants.misc.LOCAL_GPU_DISCOUNT_PERCENT;
  const resolvedCloudLabel = cloudLabel ?? t('tokens.cost', { defaultValue: 'Cost' });

  // Dual Cloud + Local GPU columns (desktop, local-capable provider).
  if (showLocal) {
    return (
      <div className="flex items-start justify-end gap-4 sm:gap-6">
        <div className="text-right">
          <div className="text-xs text-white/40 flex items-center justify-end gap-1">
            <Cloud className="h-3 w-3" />
            {t('tokens.cloud', { defaultValue: 'Cloud' })}
            {loading && <Loader2 className="h-3 w-3 animate-spin text-white/40 ml-0.5" />}
          </div>
          <div className="text-lg font-semibold text-white/60">
            {prefix}
            {cloudTokens.toLocaleString()} tokens
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-white/40 flex items-center justify-end gap-1">
            <Cpu className="h-3 w-3 text-green-400" />
            {t('tokens.localGpu', { discount: localGpuDiscount })}
          </div>
          <div className={`text-lg font-semibold ${hasEnough ? 'text-green-400' : 'text-red-400'}`}>
            {localTokens === 0
              ? t('tokens.free', { defaultValue: 'Free' })
              : `${prefix}${localTokens.toLocaleString()} tokens`}
          </div>
        </div>
      </div>
    );
  }

  // Single cloud value (web serverless, or a cloud-only provider on desktop).
  return (
    <div className="text-right">
      <div className="text-xs text-white/40 flex items-center justify-end gap-1">
        <Cloud className="h-3 w-3" />
        {resolvedCloudLabel}
        {loading && <Loader2 className="h-3 w-3 animate-spin text-white/40 ml-0.5" />}
      </div>
      <div className={`text-lg font-semibold ${hasEnough ? 'text-green-400' : 'text-red-400'}`}>
        {prefix}
        {cloudTokens.toLocaleString()} tokens
      </div>
    </div>
  );
});

export default CloudLocalCostDisplay;
